import { AxiosError } from 'axios';
import type {
  Booking,
  BookingErrorCode,
  BookingLists,
  BookingResult,
  CreateBookingInput,
  JoinBookingResult,
  SlotsResponse,
} from '../types/booking';
import type {
  HubCell,
  HubFilter,
  HubPartKey,
  HubPeople,
  HubTab,
  HubTimes,
} from '../types/booking-hub';
import { deviceTimeZone } from '../utils/listening';
import api from './api';

/**
 * Booked 1:1 calls. Every call RETURNS its refusal (class B): the server's
 * `error` code and `message` reach the caller, which shows the sentence
 * where it belongs (under the Book button, on the booked screen).
 */

const FALLBACK = "Couldn't reach the server. Check your connection and try again.";

function refusal<T>(error: unknown): BookingResult<T> {
  if (error instanceof AxiosError && error.response?.data) {
    const data = error.response.data as { error?: string; message?: string };
    if (typeof data.error === 'string') {
      return {
        success: false,
        error: data.error as BookingErrorCode,
        message: data.message ?? FALLBACK,
      };
    }
  }
  return { success: false, error: 'network', message: FALLBACK };
}

/**
 * The giver's bookable starts for the viewer. No `duration` on the first
 * read: the server answers 30 when they offer it, else their shortest, with
 * `offered_durations` for the Length chips.
 */
export async function getSlots(
  giverId: number,
  duration?: number | null,
): Promise<BookingResult<SlotsResponse>> {
  const params: Record<string, string | number> = {};
  if (duration) params.duration = duration;
  const tz = deviceTimeZone();
  if (tz) params.tz = tz;
  try {
    const response = await api.get<{ data: SlotsResponse }>(`/users/${giverId}/slots`, { params });
    return { success: true, data: response.data.data };
  } catch (error) {
    return refusal(error);
  }
}

export async function createBooking(input: CreateBookingInput): Promise<BookingResult<Booking>> {
  try {
    const tz = input.tz ?? deviceTimeZone();
    const response = await api.post<{ data: { booking: Booking } }>('/bookings', {
      ...input,
      ...(tz ? { tz } : {}),
    });
    return { success: true, data: response.data.data.booking };
  } catch (error) {
    return refusal(error);
  }
}

export async function listBookings(): Promise<BookingResult<BookingLists>> {
  try {
    const response = await api.get<{ data: BookingLists }>('/bookings');
    return { success: true, data: response.data.data };
  } catch (error) {
    return refusal(error);
  }
}

export async function getBooking(id: number): Promise<BookingResult<Booking>> {
  try {
    const response = await api.get<{ data: { booking: Booking } }>(`/bookings/${id}`);
    return { success: true, data: response.data.data.booking };
  } catch (error) {
    return refusal(error);
  }
}

export async function cancelBooking(
  id: number,
  reason?: string | null,
): Promise<BookingResult<Booking>> {
  try {
    const trimmed = reason?.trim();
    const response = await api.post<{ data: { booking: Booking } }>(
      `/bookings/${id}/cancel`,
      trimmed ? { reason: trimmed } : {},
    );
    return { success: true, data: response.data.data.booking };
  } catch (error) {
    return refusal(error);
  }
}

/**
 * Where a booking can move to (the Reschedule sheet): the slots answer for
 * the booking's pair and length, its own start listed as `current`. Every
 * start in `slots` is one `rescheduleBooking` accepts (server class C).
 */
export async function getRescheduleSlots(id: number): Promise<BookingResult<SlotsResponse>> {
  const tz = deviceTimeZone();
  try {
    const response = await api.get<{ data: SlotsResponse }>(`/bookings/${id}/slots`, {
      params: tz ? { tz } : {},
    });
    return { success: true, data: response.data.data };
  } catch (error) {
    return refusal(error);
  }
}

/**
 * Move a booking to `startsAt` (a UTC instant from getRescheduleSlots). The
 * server books the new time and releases the old one in one step under its
 * lock; a refusal (slot_taken, too_soon, too_late_to_move, …) carries the
 * sentence to show.
 */
export async function rescheduleBooking(
  id: number,
  startsAt: string,
): Promise<BookingResult<Booking>> {
  try {
    const response = await api.post<{ data: { booking: Booking } }>(`/bookings/${id}/reschedule`, {
      starts_at: startsAt,
    });
    return { success: true, data: response.data.data.booking };
  } catch (error) {
    return refusal(error);
  }
}

/**
 * The booked call's "Join" (D32). The route carries the 1:1 join gates
 * (circle agreement, "How empathy calls work"); the API layer raises their
 * sheets on the 403 and replays, so a resolved success means both passed.
 */
export async function joinBooking(id: number): Promise<BookingResult<JoinBookingResult>> {
  try {
    const response = await api.post<{ data: JoinBookingResult }>(`/bookings/${id}/join`);
    return { success: true, data: response.data.data };
  } catch (error) {
    return refusal(error);
  }
}

/**
 * Book a call hub (MY-BOOKINGS.md PR B): Pick a time's heatmap for a week.
 * Aggregated on the server (one request; no per-listener reads here).
 */
export async function getHubTimes(input: {
  filter: HubFilter;
  week: number;
  duration?: number;
}): Promise<BookingResult<HubTimes>> {
  const params: Record<string, string | number> = { filter: input.filter, week: input.week };
  if (input.duration) params.duration = input.duration;
  const tz = deviceTimeZone();
  if (tz) params.tz = tz;
  try {
    const response = await api.get<{ data: HubTimes }>('/book/times', { params });
    return { success: true, data: response.data.data };
  } catch (error) {
    return refusal(error);
  }
}

/** Who is free in one heatmap cell, with their starts there. */
export async function getHubCell(input: {
  filter: HubFilter;
  date: string;
  part: HubPartKey;
  duration?: number;
}): Promise<BookingResult<HubCell>> {
  const params: Record<string, string | number> = {
    filter: input.filter,
    date: input.date,
    part: input.part,
  };
  if (input.duration) params.duration = input.duration;
  const tz = deviceTimeZone();
  if (tz) params.tz = tz;
  try {
    const response = await api.get<{ data: HubCell }>('/book/times/cell', { params });
    return { success: true, data: response.data.data };
  } catch (error) {
    return refusal(error);
  }
}

/** Pick a person's rows for a tab (tab `talked` is also the hub's Talk again). */
export async function getHubPeople(input: {
  tab: HubTab;
  q?: string;
  limit?: number;
}): Promise<BookingResult<HubPeople>> {
  const params: Record<string, string | number> = { tab: input.tab };
  if (input.q) params.q = input.q;
  if (input.limit) params.limit = input.limit;
  const tz = deviceTimeZone();
  if (tz) params.tz = tz;
  try {
    const response = await api.get<{ data: HubPeople }>('/book/people', { params });
    return { success: true, data: response.data.data };
  } catch (error) {
    return refusal(error);
  }
}
